/**
 * Journey 工作區 V2（任務首頁、工作區導覽、旅程狀態）文案。
 * 英文複數使用 `<key>_one`；中文只提供 key 本身。
 */
import { createFlatMessageLookup } from './flatMessages';

const P = 'travel.journeyWorkspace';

export const journeyWorkspaceMessagesZhTW = {
  [`${P}.loading`]: '載入中…',
  [`${P}.map.title`]: '旅程地圖',
  [`${P}.map.hint`]: '查看有座標的活動與住宿分布；交通目前以文字顯示出發地與目的地（唯讀）。',
  [`${P}.map.status.offlineUnknown`]: '此旅程的地圖資料尚未離線保存，連線後即可載入。',
  [`${P}.map.status.loading`]: '正在載入旅程地理資訊…',
  [`${P}.map.status.unavailable`]: '暫時無法載入旅程地理資訊；總覽其他內容仍可使用。',
  [`${P}.map.status.empty`]: '此旅程尚未有活動、住宿或交通安排。',
  [`${P}.map.status.noCoordinates`]: '目前沒有可標示在地圖上的座標；下方仍列出行程位置與文字規劃。',
  [`${P}.map.status.noCoordinatesFiltered`]: '此篩選範圍沒有可標示在地圖上的座標；清單仍會顯示相關項目。',
  [`${P}.map.status.layersOff`]: '目前圖層都已關閉；重新開啟「活動」或「住宿」即可看到地圖標記。',
  [`${P}.map.filters`]: '旅程地圖篩選',
  [`${P}.map.filter.all`]: '全部',
  [`${P}.map.filter.stays`]: '住宿',
  [`${P}.map.current`]: '（目前）',
  [`${P}.map.layers`]: '地圖圖層',
  [`${P}.map.layer.activity`]: '活動',
  [`${P}.map.layer.stay`]: '住宿',
  [`${P}.map.legend`]: '圖例：活＝活動、宿＝住宿。交通尚無座標，不會出現在地圖上。',
  [`${P}.map.listTitle`]: '行程位置清單',
  [`${P}.map.selected`]: '（已選取）',
  [`${P}.map.badge.stay`]: '住宿',
  [`${P}.map.badge.cancelled`]: '已取消',
  [`${P}.map.badge.transport`]: '交通',
  [`${P}.map.noLocation`]: '未設定地點',
  [`${P}.map.noCoordinates`]: '無座標',
  [`${P}.map.transportTitle`]: '交通規劃（文字）',
  [`${P}.map.transportNote`]: '目前交通資料沒有地圖座標，因此不會建立標記；以下仍完整保留出發地與目的地。',
  [`${P}.map.noMarker`]: '無地圖標記',
  [`${P}.map.mapEmpty`]: '目前沒有可標示在地圖上的座標。',
  [`${P}.map.markersLabel`]: '旅程地圖標記',
  [`${P}.map.offline`]: '地圖需要網路連線',
  [`${P}.map.ctaPlanning`]: '前往規劃',
  [`${P}.map.ctaStays`]: '管理住宿',
  [`${P}.map.ctaTransport`]: '規劃交通',
  [`${P}.chrome.backToMyJourneys`]: '返回我的行程',
  [`${P}.chrome.cancel`]: '取消',
  [`${P}.chrome.coverAlt`]: '行程封面',
  [`${P}.chrome.coverEmpty`]: '尚未設定行程封面。',
  [`${P}.chrome.coverSectionLabel`]: '行程封面',
  [`${P}.chrome.coverUploadLabel`]: '上傳封面圖片',
  [`${P}.chrome.coverUploading`]: '上傳中…',
  [`${P}.chrome.coverFailed`]: '無法更新封面，請再試一次。',
  [`${P}.chrome.created`]: '建立時間',
  [`${P}.chrome.dates`]: '日期',
  [`${P}.chrome.days`]: '天數',
  [`${P}.chrome.description`]: '說明',
  [`${P}.chrome.destination`]: '目的地',
  [`${P}.chrome.editBasicInfo`]: '編輯基本資訊',
  [`${P}.chrome.endDate`]: '結束日期',
  [`${P}.chrome.eyebrow`]: '旅程工作區',
  [`${P}.chrome.forbiddenBody`]: '你沒有權限查看這趟私人行程。',
  [`${P}.chrome.forbiddenTitle`]: '無法開啟此行程',
  [`${P}.chrome.myJourneys`]: '我的行程',
  [`${P}.chrome.noDescription`]: '尚未填寫說明。',
  [`${P}.chrome.notFoundBody`]: '此行程可能不存在，或目前無法查看。',
  [`${P}.chrome.notFoundTitle`]: '找不到此行程',
  [`${P}.chrome.notSet`]: '尚未設定',
  [`${P}.chrome.openFailedTitle`]: '無法開啟此行程',
  [`${P}.chrome.overview`]: '總覽',
  [`${P}.chrome.people`]: '旅伴',
  [`${P}.chrome.peopleCount`]: '人數',
  [`${P}.chrome.saveChanges`]: '儲存變更',
  [`${P}.chrome.saving`]: '儲存中…',
  [`${P}.chrome.startDate`]: '開始日期',
  [`${P}.chrome.status`]: '狀態',
  [`${P}.chrome.summary`]: '摘要',
  [`${P}.chrome.title`]: '標題',
  [`${P}.chrome.tryAgain`]: '再試一次',
  [`${P}.chrome.updated`]: '更新時間',
  [`${P}.chrome.yourRole`]: '你的角色',
  [`${P}.chrome.role.OWNER`]: '擁有者',
  [`${P}.chrome.role.COORDINATOR`]: '協調者',
  [`${P}.chrome.role.MEMBER`]: '成員',
  [`${P}.chrome.save.titleRequired`]: '請輸入標題。',
  [`${P}.chrome.save.endBeforeStart`]: '結束日期不能早於開始日期。',
  [`${P}.chrome.save.conflict`]: '這趟旅程已在其他地方更新，請重新整理後再試。',
  [`${P}.chrome.save.validation`]: '請檢查旅程資料。',
  [`${P}.chrome.save.forbidden`]: '你沒有權限編輯這趟旅程。',
  [`${P}.updating`]: '更新中…',

  [`${P}.guide.allTools`]: '自由編輯／所有工具',
  [`${P}.guide.prepare.heading`]: '規劃起步',
  [`${P}.guide.prepare.progress`]: '規劃起步：已完成 {done}／{total} 項',
  [`${P}.guide.prepare.complete`]: '行程已起步，可繼續完善每日安排。',
  [`${P}.guide.prepare.unknownNote`]: '部分安排暫時無法讀取，進度只計算已確認的項目。',
  [`${P}.guide.prepare.nextStep`]: '下一步',
  [`${P}.guide.prepare.nextStepOptional`]: '建議下一步（選填）',
  [`${P}.guide.prepare.upNext`]: '接下來',
  [`${P}.guide.task.basics.title`]: '基本資料',
  [`${P}.guide.task.basics.description`]: '填好目的地與出發、回程日期，旅伴與之後的安排才有依據。',
  [`${P}.guide.task.basics.done`]: '已完成',
  [`${P}.guide.task.basics.todo`]: '尚未完成：缺少目的地或日期',
  [`${P}.guide.task.basics.action`]: '填寫目的地與日期',
  [`${P}.guide.task.basics.unavailable`]: '由擁有者或協調者設定基本資料。',
  [`${P}.guide.task.activities.title`]: '安排景點／活動',
  [`${P}.guide.task.activities.description`]: '在規劃頁把想去的景點、餐廳或活動排進每一天。',
  [`${P}.guide.task.activities.done`]: '已完成：已安排 {count} 項',
  [`${P}.guide.task.activities.todo`]: '尚未完成：還沒有任何安排',
  [`${P}.guide.task.activities.unknown`]: '目前無法確認安排狀態，可到規劃頁查看',
  [`${P}.guide.task.activities.emptyDays`]: '還有 {count} 天尚未安排（選填）',
  [`${P}.guide.task.activities.actionStart`]: '前往規劃頁安排行程',
  [`${P}.guide.task.activities.actionContinue`]: '繼續安排每天的行程',
  [`${P}.guide.task.logistics.title`]: '住宿與交通',
  [`${P}.guide.task.logistics.description`]: '記下住宿與交通安排；當天來回或自行前往可以不填。',
  [`${P}.guide.task.logistics.added`]: '選填・已加入住宿 {stays} 筆、交通 {transports} 筆',
  [`${P}.guide.task.logistics.empty`]: '選填・尚未加入',
  [`${P}.guide.task.logistics.actionStays`]: '前往住宿頁新增住宿',
  [`${P}.guide.task.logistics.actionTransport`]: '前往交通頁',
  [`${P}.guide.task.departure.title`]: '出發準備',
  [`${P}.guide.task.departure.description`]: '用行前清單整理證件、行李與待辦，完成度請在清單頁查看。',
  [`${P}.guide.task.departure.optional`]: '選填',
  [`${P}.guide.task.departure.action`]: '前往行前清單',
  [`${P}.guide.traveling.heading`]: '旅程進行中',
  [`${P}.guide.traveling.eyebrow`]: '今天',
  [`${P}.guide.traveling.title`]: '查看今日行程，順手記錄',
  [`${P}.guide.traveling.body`]: '今日頁會列出今天的安排；想留下心得或照片，可以到日記記錄。',
  [`${P}.guide.traveling.actionToday`]: '查看今日行程',
  [`${P}.guide.completed.heading`]: '旅程已完成',
  [`${P}.guide.completed.eyebrow`]: '整理與分享',
  [`${P}.guide.completed.title`]: '整理回憶，決定是否分享',
  [`${P}.guide.completed.body`]: '可以補寫日記；若想讓其他人參考，可在公開旅程設定分享。分享不是必要步驟。',
  [`${P}.guide.completed.actionJournal`]: '整理旅程日記',
  [`${P}.guide.completed.actionPublication`]: '查看公開分享設定',
  [`${P}.guide.closed.archivedHeading`]: '旅程已封存',
  [`${P}.guide.closed.cancelledHeading`]: '旅程已取消',
  [`${P}.guide.closed.archivedBody`]: '這趟旅程已封存，不需要再準備，內容僅供回顧。',
  [`${P}.guide.closed.cancelledBody`]: '這趟旅程已取消，不需要再準備；可用的操作請見下方旅程狀態。',
  [`${P}.recordJourney`]: '記錄這趟旅程',

  [`${P}.nav.label`]: '工作區導覽',
  [`${P}.nav.toolsShow`]: '所有工具／自由編輯',
  [`${P}.nav.toolsHide`]: '收合所有工具',
  [`${P}.nav.unread`]: '{count} 則未讀旅程通知',
  [`${P}.nav.current`]: '目前',
  [`${P}.nav.offlineSuffix`]: '（需要網路連線）',
  [`${P}.nav.networkRequired`]: '需要網路連線',
  [`${P}.nav.group.plan`]: '行程安排',
  [`${P}.nav.group.collab`]: '協作',
  [`${P}.nav.group.memory`]: '紀錄',
  [`${P}.nav.group.money`]: '費用',
  [`${P}.nav.item.overview`]: '總覽',
  [`${P}.nav.item.planning`]: '規劃',
  [`${P}.nav.item.today`]: '今日',
  [`${P}.nav.item.people`]: '旅伴',
  [`${P}.nav.item.stays`]: '住宿',
  [`${P}.nav.item.transport`]: '交通',
  [`${P}.nav.item.checklist`]: '清單',
  [`${P}.nav.item.timeline`]: '動態',
  [`${P}.nav.item.notices`]: '通知',
  [`${P}.nav.item.chat`]: '旅程聊天室',
  [`${P}.nav.item.decisions`]: '決定',
  [`${P}.nav.item.situations`]: '旅程狀況',
  [`${P}.nav.item.journal`]: '日記',
  [`${P}.nav.item.budget`]: '預算',
  [`${P}.nav.item.expenses`]: '支出',
  [`${P}.nav.item.split`]: '分帳',

  [`${P}.allTools.title`]: '接著安排行程',
  [`${P}.allTools.offlineDetail`]: '規劃、住宿與協作頁面需要網路連線。',
  [`${P}.allTools.intro`]: '可前往以下頁面：',
  [`${P}.allTools.planning`]: '安排日程與活動',
  [`${P}.allTools.stays`]: '管理住宿',
  [`${P}.allTools.transport`]: '規劃交通',
  [`${P}.allTools.checklist`]: '準備行前清單',
  [`${P}.allTools.budget`]: '整理預算',
  [`${P}.allTools.decisions`]: '旅伴一起決定',

  [`${P}.lifecycle.heading`]: '旅程狀態',
  [`${P}.lifecycle.secondaryLabel`]: '其他狀態操作',
  [`${P}.lifecycle.closeConfirm`]: '關閉確認',
  [`${P}.lifecycle.cancelReason`]: '取消原因（選填）',
  [`${P}.lifecycle.back`]: '返回',
  [`${P}.lifecycle.status.DRAFT`]: '規劃中',
  [`${P}.lifecycle.status.READY`]: '準備出發',
  [`${P}.lifecycle.status.TRAVELING`]: '旅程進行中',
  [`${P}.lifecycle.status.COMPLETED`]: '旅程已完成',
  [`${P}.lifecycle.status.CANCELLED`]: '旅程已取消',
  [`${P}.lifecycle.status.ARCHIVED`]: '已封存',
  [`${P}.lifecycle.status.unknown`]: '未知狀態',
  [`${P}.lifecycle.hint.DRAFT`]: '完成基本安排後，可以將旅程標記為準備完成。',
  [`${P}.lifecycle.hint.READY`]: '行程已準備完成。出發時開始旅程。',
  [`${P}.lifecycle.hint.TRAVELING`]: '旅程進行中，可持續調整安排並記錄日記。',
  [`${P}.lifecycle.hint.COMPLETED`]: '你仍可以補寫日記、整理照片與分享旅程。',
  [`${P}.lifecycle.hint.CANCELLED`]: '此旅程已取消，無法繼續一般行程操作。',
  [`${P}.lifecycle.hint.ARCHIVED`]: '此旅程已封存，僅供回顧。',
  [`${P}.lifecycle.hint.unknown`]: '目前狀態無法進行旅程狀態變更。',
  [`${P}.lifecycle.action.READY`]: '準備完成',
  [`${P}.lifecycle.action.START`]: '開始旅程',
  [`${P}.lifecycle.action.COMPLETE`]: '完成旅程',
  [`${P}.lifecycle.action.CANCEL`]: '取消旅程',
  [`${P}.lifecycle.action.ARCHIVE`]: '封存旅程',
  [`${P}.lifecycle.confirmAction.READY`]: '確認準備完成',
  [`${P}.lifecycle.confirmAction.START`]: '確認開始旅程',
  [`${P}.lifecycle.confirmAction.COMPLETE`]: '確認完成旅程',
  [`${P}.lifecycle.confirmAction.CANCEL`]: '確認取消旅程',
  [`${P}.lifecycle.confirmAction.ARCHIVE`]: '確認封存旅程',
  [`${P}.lifecycle.confirmTitle.READY`]: '確認準備完成？',
  [`${P}.lifecycle.confirmBody.READY`]: '確認將這趟旅程標記為準備完成？之後即可建立決定與書寫日記。',
  [`${P}.lifecycle.confirmTitle.START`]: '確認開始旅程？',
  [`${P}.lifecycle.confirmBody.START`]: '確認開始這趟旅程？開始後會進入旅程進行中狀態。',
  [`${P}.lifecycle.confirmTitle.COMPLETE`]: '確認完成旅程？',
  [`${P}.lifecycle.confirmBody.COMPLETE`]: '確認完成這趟旅程？完成後仍可補寫日記。',
  [`${P}.lifecycle.confirmTitle.CANCEL`]: '確認取消旅程？',
  [`${P}.lifecycle.confirmBody.CANCEL`]: '取消後旅程會進入已取消狀態，一般規劃與出發流程將停止。此操作需由擁有者執行。',
  [`${P}.lifecycle.confirmTitle.ARCHIVE`]: '確認封存旅程？',
  [`${P}.lifecycle.confirmBody.ARCHIVE`]: '封存後無法再變更旅程狀態（不可逆）。僅已完成或已取消的旅程可封存。',
  [`${P}.lifecycle.confirmTitle.default`]: '確認變更旅程狀態？',
  [`${P}.lifecycle.confirmBody.default`]: '請確認要繼續。',
  [`${P}.lifecycle.confirmAction.default`]: '確認',
  [`${P}.lifecycle.error.conflict`]: '這趟旅程剛剛有新的變更，已重新載入最新狀態。',
  [`${P}.lifecycle.error.invalid-lifecycle`]: '目前狀態無法執行此操作，已重新載入最新狀態。',
  [`${P}.lifecycle.error.forbidden`]: '你沒有權限變更這趟旅程的狀態。',
  [`${P}.lifecycle.error.not-found`]: '找不到這趟旅程。',
  [`${P}.lifecycle.error.unauthorized`]: '登入狀態已失效，請重新登入。',
  [`${P}.lifecycle.error.validation`]: '請確認後再試。',
  [`${P}.lifecycle.error.network`]: '網路異常，請稍後再試。',
  [`${P}.lifecycle.error.generic`]: '無法更新旅程狀態，請稍後再試。',
};

export const journeyWorkspaceMessagesEnUS = {
  [`${P}.loading`]: 'Loading…',
  [`${P}.map.title`]: 'Trip map',
  [`${P}.map.hint`]: 'See where activities and stays with coordinates are. Transport is shown as text with origin and destination (read-only).',
  [`${P}.map.status.offlineUnknown`]: 'This trip’s map data isn’t saved offline yet. It will load once you’re online.',
  [`${P}.map.status.loading`]: 'Loading trip locations…',
  [`${P}.map.status.unavailable`]: 'Trip locations can’t be loaded right now. The rest of the overview still works.',
  [`${P}.map.status.empty`]: 'This trip has no activities, stays, or transport yet.',
  [`${P}.map.status.noCoordinates`]: 'Nothing has map coordinates yet. Locations and text plans are still listed below.',
  [`${P}.map.status.noCoordinatesFiltered`]: 'Nothing in this filter has map coordinates. The list still shows the items.',
  [`${P}.map.status.layersOff`]: 'All layers are off. Turn on Activities or Stays to see map markers.',
  [`${P}.map.filters`]: 'Trip map filters',
  [`${P}.map.filter.all`]: 'All',
  [`${P}.map.filter.stays`]: 'Stays',
  [`${P}.map.current`]: ' (current)',
  [`${P}.map.layers`]: 'Map layers',
  [`${P}.map.layer.activity`]: 'Activities',
  [`${P}.map.layer.stay`]: 'Stays',
  [`${P}.map.legend`]: 'Legend: 活 = activity, 宿 = stay. Transport has no coordinates and isn’t shown on the map.',
  [`${P}.map.listTitle`]: 'Trip locations',
  [`${P}.map.selected`]: ' (selected)',
  [`${P}.map.badge.stay`]: 'Stay',
  [`${P}.map.badge.cancelled`]: 'Cancelled',
  [`${P}.map.badge.transport`]: 'Transport',
  [`${P}.map.noLocation`]: 'No location set',
  [`${P}.map.noCoordinates`]: 'No coordinates',
  [`${P}.map.transportTitle`]: 'Transport (text only)',
  [`${P}.map.transportNote`]: 'Transport has no map coordinates, so no markers are created. Origins and destinations are listed below.',
  [`${P}.map.noMarker`]: 'No map marker',
  [`${P}.map.mapEmpty`]: 'Nothing has map coordinates yet.',
  [`${P}.map.markersLabel`]: 'Trip map markers',
  [`${P}.map.offline`]: 'The map needs a connection',
  [`${P}.map.ctaPlanning`]: 'Go to planning',
  [`${P}.map.ctaStays`]: 'Manage stays',
  [`${P}.map.ctaTransport`]: 'Plan transport',
  [`${P}.chrome.backToMyJourneys`]: 'Back to My trips',
  [`${P}.chrome.cancel`]: 'Cancel',
  [`${P}.chrome.coverAlt`]: 'Trip cover',
  [`${P}.chrome.coverEmpty`]: 'No cover image yet.',
  [`${P}.chrome.coverSectionLabel`]: 'Trip cover',
  [`${P}.chrome.coverUploadLabel`]: 'Upload cover image',
  [`${P}.chrome.coverUploading`]: 'Uploading…',
  [`${P}.chrome.coverFailed`]: 'Couldn’t update the cover. Please try again.',
  [`${P}.chrome.created`]: 'Created',
  [`${P}.chrome.dates`]: 'Dates',
  [`${P}.chrome.days`]: 'Days',
  [`${P}.chrome.description`]: 'Description',
  [`${P}.chrome.destination`]: 'Destination',
  [`${P}.chrome.editBasicInfo`]: 'Edit basics',
  [`${P}.chrome.endDate`]: 'End date',
  [`${P}.chrome.eyebrow`]: 'Trip workspace',
  [`${P}.chrome.forbiddenBody`]: 'You don’t have permission to view this private trip.',
  [`${P}.chrome.forbiddenTitle`]: 'Can’t open this trip',
  [`${P}.chrome.myJourneys`]: 'My trips',
  [`${P}.chrome.noDescription`]: 'No description yet.',
  [`${P}.chrome.notFoundBody`]: 'This trip may not exist or can’t be viewed right now.',
  [`${P}.chrome.notFoundTitle`]: 'Trip not found',
  [`${P}.chrome.notSet`]: 'Not set',
  [`${P}.chrome.openFailedTitle`]: 'Can’t open this trip',
  [`${P}.chrome.overview`]: 'Overview',
  [`${P}.chrome.people`]: 'People',
  [`${P}.chrome.peopleCount`]: 'People',
  [`${P}.chrome.saveChanges`]: 'Save changes',
  [`${P}.chrome.saving`]: 'Saving…',
  [`${P}.chrome.startDate`]: 'Start date',
  [`${P}.chrome.status`]: 'Status',
  [`${P}.chrome.summary`]: 'Summary',
  [`${P}.chrome.title`]: 'Title',
  [`${P}.chrome.tryAgain`]: 'Try again',
  [`${P}.chrome.updated`]: 'Updated',
  [`${P}.chrome.yourRole`]: 'Your role',
  [`${P}.chrome.role.OWNER`]: 'Owner',
  [`${P}.chrome.role.COORDINATOR`]: 'Coordinator',
  [`${P}.chrome.role.MEMBER`]: 'Member',
  [`${P}.chrome.save.titleRequired`]: 'Title is required.',
  [`${P}.chrome.save.endBeforeStart`]: 'End date must be on or after the start date.',
  [`${P}.chrome.save.conflict`]: 'This trip changed elsewhere. Refresh and try again.',
  [`${P}.chrome.save.validation`]: 'Please check the trip details.',
  [`${P}.chrome.save.forbidden`]: 'You don’t have permission to edit this trip.',
  [`${P}.updating`]: 'Updating…',

  [`${P}.guide.allTools`]: 'All tools & editing',
  [`${P}.guide.prepare.heading`]: 'Getting started',
  [`${P}.guide.prepare.progress`]: 'Getting started: {done} of {total} done',
  [`${P}.guide.prepare.complete`]: 'Your trip is off to a good start. Keep refining each day.',
  [`${P}.guide.prepare.unknownNote`]: 'Some plans can’t be loaded right now. Progress only counts confirmed items.',
  [`${P}.guide.prepare.nextStep`]: 'Next step',
  [`${P}.guide.prepare.nextStepOptional`]: 'Suggested next step (optional)',
  [`${P}.guide.prepare.upNext`]: 'Up next',
  [`${P}.guide.task.basics.title`]: 'Trip basics',
  [`${P}.guide.task.basics.description`]: 'Add the destination and travel dates so companions and later plans have something to go on.',
  [`${P}.guide.task.basics.done`]: 'Done',
  [`${P}.guide.task.basics.todo`]: 'Not done: destination or dates missing',
  [`${P}.guide.task.basics.action`]: 'Add destination and dates',
  [`${P}.guide.task.basics.unavailable`]: 'The owner or a coordinator sets the trip basics.',
  [`${P}.guide.task.activities.title`]: 'Plan places and activities',
  [`${P}.guide.task.activities.description`]: 'Use the planning page to add places, restaurants, or activities to each day.',
  [`${P}.guide.task.activities.done_one`]: 'Done: {count} activity planned',
  [`${P}.guide.task.activities.done`]: 'Done: {count} activities planned',
  [`${P}.guide.task.activities.todo`]: 'Not done: nothing planned yet',
  [`${P}.guide.task.activities.unknown`]: 'Can’t confirm your plans right now. Check the planning page.',
  [`${P}.guide.task.activities.emptyDays_one`]: '{count} more day has nothing planned (optional)',
  [`${P}.guide.task.activities.emptyDays`]: '{count} more days have nothing planned (optional)',
  [`${P}.guide.task.activities.actionStart`]: 'Go to planning',
  [`${P}.guide.task.activities.actionContinue`]: 'Keep planning each day',
  [`${P}.guide.task.logistics.title`]: 'Stays and transport',
  [`${P}.guide.task.logistics.description`]: 'Note where you’ll stay and how you’ll get around. Skip this for day trips or if you’re making your own way.',
  [`${P}.guide.task.logistics.added`]: 'Optional · Stays: {stays} · Transport: {transports}',
  [`${P}.guide.task.logistics.empty`]: 'Optional · Nothing added yet',
  [`${P}.guide.task.logistics.actionStays`]: 'Add a stay',
  [`${P}.guide.task.logistics.actionTransport`]: 'Go to transport',
  [`${P}.guide.task.departure.title`]: 'Get ready to go',
  [`${P}.guide.task.departure.description`]: 'Use the checklist for documents, packing, and to-dos. Track progress on the checklist page.',
  [`${P}.guide.task.departure.optional`]: 'Optional',
  [`${P}.guide.task.departure.action`]: 'Open checklist',
  [`${P}.guide.traveling.heading`]: 'Trip in progress',
  [`${P}.guide.traveling.eyebrow`]: 'Today',
  [`${P}.guide.traveling.title`]: 'Check today’s plan and jot things down',
  [`${P}.guide.traveling.body`]: 'The Today page lists today’s plans. Add thoughts or photos in the journal.',
  [`${P}.guide.traveling.actionToday`]: 'View today’s plan',
  [`${P}.guide.completed.heading`]: 'Trip completed',
  [`${P}.guide.completed.eyebrow`]: 'Wrap up and share',
  [`${P}.guide.completed.title`]: 'Sort your memories and decide whether to share',
  [`${P}.guide.completed.body`]: 'You can still add journal entries. To help others plan, set up sharing under Public journey. Sharing is optional.',
  [`${P}.guide.completed.actionJournal`]: 'Review the journal',
  [`${P}.guide.completed.actionPublication`]: 'View sharing settings',
  [`${P}.guide.closed.archivedHeading`]: 'Trip archived',
  [`${P}.guide.closed.cancelledHeading`]: 'Trip cancelled',
  [`${P}.guide.closed.archivedBody`]: 'This trip is archived. There’s nothing left to prepare; it’s kept for looking back.',
  [`${P}.guide.closed.cancelledBody`]: 'This trip was cancelled. There’s nothing left to prepare; see Trip status below for available actions.',
  [`${P}.recordJourney`]: 'Write in the journal',

  [`${P}.nav.label`]: 'Trip workspace',
  [`${P}.nav.toolsShow`]: 'All tools & editing',
  [`${P}.nav.toolsHide`]: 'Hide all tools',
  [`${P}.nav.unread_one`]: '{count} unread trip notice',
  [`${P}.nav.unread`]: '{count} unread trip notices',
  [`${P}.nav.current`]: 'Current',
  [`${P}.nav.offlineSuffix`]: ' (needs a connection)',
  [`${P}.nav.networkRequired`]: 'Needs a connection',
  [`${P}.nav.group.plan`]: 'Logistics',
  [`${P}.nav.group.collab`]: 'Together',
  [`${P}.nav.group.memory`]: 'Memories',
  [`${P}.nav.group.money`]: 'Costs',
  [`${P}.nav.item.overview`]: 'Overview',
  [`${P}.nav.item.planning`]: 'Plan',
  [`${P}.nav.item.today`]: 'Today',
  [`${P}.nav.item.people`]: 'People',
  [`${P}.nav.item.stays`]: 'Stays',
  [`${P}.nav.item.transport`]: 'Transport',
  [`${P}.nav.item.checklist`]: 'Checklist',
  [`${P}.nav.item.timeline`]: 'Activity',
  [`${P}.nav.item.notices`]: 'Notices',
  [`${P}.nav.item.chat`]: 'Chat',
  [`${P}.nav.item.decisions`]: 'Decisions',
  [`${P}.nav.item.situations`]: 'Situations',
  [`${P}.nav.item.journal`]: 'Journal',
  [`${P}.nav.item.budget`]: 'Budget',
  [`${P}.nav.item.expenses`]: 'Expenses',
  [`${P}.nav.item.split`]: 'Split',

  [`${P}.allTools.title`]: 'Keep planning',
  [`${P}.allTools.offlineDetail`]: 'Planning, stays, and collaboration pages need a connection.',
  [`${P}.allTools.intro`]: 'Go to:',
  [`${P}.allTools.planning`]: 'Schedule days and activities',
  [`${P}.allTools.stays`]: 'Manage where you’ll stay',
  [`${P}.allTools.transport`]: 'Plan how you’ll get around',
  [`${P}.allTools.checklist`]: 'Prepare your checklist',
  [`${P}.allTools.budget`]: 'Keep the budget in order',
  [`${P}.allTools.decisions`]: 'Decide together',

  [`${P}.lifecycle.heading`]: 'Trip status',
  [`${P}.lifecycle.secondaryLabel`]: 'Other status actions',
  [`${P}.lifecycle.closeConfirm`]: 'Close dialog',
  [`${P}.lifecycle.cancelReason`]: 'Reason for cancelling (optional)',
  [`${P}.lifecycle.back`]: 'Back',
  [`${P}.lifecycle.status.DRAFT`]: 'Planning',
  [`${P}.lifecycle.status.READY`]: 'Ready to go',
  [`${P}.lifecycle.status.TRAVELING`]: 'Trip in progress',
  [`${P}.lifecycle.status.COMPLETED`]: 'Trip completed',
  [`${P}.lifecycle.status.CANCELLED`]: 'Trip cancelled',
  [`${P}.lifecycle.status.ARCHIVED`]: 'Archived',
  [`${P}.lifecycle.status.unknown`]: 'Unknown status',
  [`${P}.lifecycle.hint.DRAFT`]: 'Once the basics are set, you can mark the trip as ready.',
  [`${P}.lifecycle.hint.READY`]: 'Your trip is ready. Start it when you set off.',
  [`${P}.lifecycle.hint.TRAVELING`]: 'Your trip is underway. Keep adjusting plans and writing in the journal.',
  [`${P}.lifecycle.hint.COMPLETED`]: 'You can still add journal entries, organize photos, and share the trip.',
  [`${P}.lifecycle.hint.CANCELLED`]: 'This trip was cancelled. Regular trip actions are no longer available.',
  [`${P}.lifecycle.hint.ARCHIVED`]: 'This trip is archived and kept for looking back.',
  [`${P}.lifecycle.hint.unknown`]: 'The trip status can’t be changed right now.',
  [`${P}.lifecycle.action.READY`]: 'Mark as ready',
  [`${P}.lifecycle.action.START`]: 'Start trip',
  [`${P}.lifecycle.action.COMPLETE`]: 'Complete trip',
  [`${P}.lifecycle.action.CANCEL`]: 'Cancel trip',
  [`${P}.lifecycle.action.ARCHIVE`]: 'Archive trip',
  [`${P}.lifecycle.confirmAction.READY`]: 'Yes, mark as ready',
  [`${P}.lifecycle.confirmAction.START`]: 'Yes, start trip',
  [`${P}.lifecycle.confirmAction.COMPLETE`]: 'Yes, complete trip',
  [`${P}.lifecycle.confirmAction.CANCEL`]: 'Yes, cancel trip',
  [`${P}.lifecycle.confirmAction.ARCHIVE`]: 'Yes, archive trip',
  [`${P}.lifecycle.confirmTitle.READY`]: 'Mark trip as ready?',
  [`${P}.lifecycle.confirmBody.READY`]: 'Once it’s ready, you can create decisions and write in the journal.',
  [`${P}.lifecycle.confirmTitle.START`]: 'Start trip?',
  [`${P}.lifecycle.confirmBody.START`]: 'The trip will move to In progress.',
  [`${P}.lifecycle.confirmTitle.COMPLETE`]: 'Complete trip?',
  [`${P}.lifecycle.confirmBody.COMPLETE`]: 'You can still add journal entries afterwards.',
  [`${P}.lifecycle.confirmTitle.CANCEL`]: 'Cancel trip?',
  [`${P}.lifecycle.confirmBody.CANCEL`]: 'The trip will be marked as cancelled, and regular planning and departure steps will stop. Only the owner can do this.',
  [`${P}.lifecycle.confirmTitle.ARCHIVE`]: 'Archive trip?',
  [`${P}.lifecycle.confirmBody.ARCHIVE`]: 'Once archived, the trip status can’t be changed again. Only completed or cancelled trips can be archived.',
  [`${P}.lifecycle.confirmTitle.default`]: 'Change trip status?',
  [`${P}.lifecycle.confirmBody.default`]: 'Please confirm to continue.',
  [`${P}.lifecycle.confirmAction.default`]: 'Confirm',
  [`${P}.lifecycle.error.conflict`]: 'This trip just changed. The latest status has been reloaded.',
  [`${P}.lifecycle.error.invalid-lifecycle`]: 'This action isn’t available in the current status. The latest status has been reloaded.',
  [`${P}.lifecycle.error.forbidden`]: 'You don’t have permission to change this trip’s status.',
  [`${P}.lifecycle.error.not-found`]: 'This trip can’t be found.',
  [`${P}.lifecycle.error.unauthorized`]: 'Your session has expired. Please sign in again.',
  [`${P}.lifecycle.error.validation`]: 'Please check and try again.',
  [`${P}.lifecycle.error.network`]: 'Network problem. Please try again later.',
  [`${P}.lifecycle.error.generic`]: 'Couldn’t update the trip status. Please try again later.',
};

export const journeyWorkspaceMessagesZhCN = {
  [`${P}.loading`]: '加载中…',
  [`${P}.map.title`]: '旅程地图',
  [`${P}.map.hint`]: '查看有坐标的活动与住宿分布；交通目前以文字显示出发地与目的地（只读）。',
  [`${P}.map.status.offlineUnknown`]: '此旅程的地图数据尚未离线保存，联网后即可加载。',
  [`${P}.map.status.loading`]: '正在加载旅程地理信息…',
  [`${P}.map.status.unavailable`]: '暂时无法加载旅程地理信息；总览其他内容仍可使用。',
  [`${P}.map.status.empty`]: '此旅程尚未有活动、住宿或交通安排。',
  [`${P}.map.status.noCoordinates`]: '目前没有可标示在地图上的坐标；下方仍列出行程位置与文字规划。',
  [`${P}.map.status.noCoordinatesFiltered`]: '此筛选范围没有可标示在地图上的坐标；清单仍会显示相关项目。',
  [`${P}.map.status.layersOff`]: '目前图层都已关闭；重新开启“活动”或“住宿”即可看到地图标记。',
  [`${P}.map.filters`]: '旅程地图筛选',
  [`${P}.map.filter.all`]: '全部',
  [`${P}.map.filter.stays`]: '住宿',
  [`${P}.map.current`]: '（当前）',
  [`${P}.map.layers`]: '地图图层',
  [`${P}.map.layer.activity`]: '活动',
  [`${P}.map.layer.stay`]: '住宿',
  [`${P}.map.legend`]: '图例：活＝活动、宿＝住宿。交通尚无坐标，不会出现在地图上。',
  [`${P}.map.listTitle`]: '行程位置清单',
  [`${P}.map.selected`]: '（已选取）',
  [`${P}.map.badge.stay`]: '住宿',
  [`${P}.map.badge.cancelled`]: '已取消',
  [`${P}.map.badge.transport`]: '交通',
  [`${P}.map.noLocation`]: '未设置地点',
  [`${P}.map.noCoordinates`]: '无坐标',
  [`${P}.map.transportTitle`]: '交通规划（文字）',
  [`${P}.map.transportNote`]: '目前交通数据没有地图坐标，因此不会建立标记；以下仍完整保留出发地与目的地。',
  [`${P}.map.noMarker`]: '无地图标记',
  [`${P}.map.mapEmpty`]: '目前没有可标示在地图上的坐标。',
  [`${P}.map.markersLabel`]: '旅程地图标记',
  [`${P}.map.offline`]: '地图需要网络连接',
  [`${P}.map.ctaPlanning`]: '前往规划',
  [`${P}.map.ctaStays`]: '管理住宿',
  [`${P}.map.ctaTransport`]: '规划交通',
  [`${P}.chrome.backToMyJourneys`]: '返回我的行程',
  [`${P}.chrome.cancel`]: '取消',
  [`${P}.chrome.coverAlt`]: '行程封面',
  [`${P}.chrome.coverEmpty`]: '尚未设置行程封面。',
  [`${P}.chrome.coverSectionLabel`]: '行程封面',
  [`${P}.chrome.coverUploadLabel`]: '上传封面图片',
  [`${P}.chrome.coverUploading`]: '上传中…',
  [`${P}.chrome.coverFailed`]: '无法更新封面，请再试一次。',
  [`${P}.chrome.created`]: '创建时间',
  [`${P}.chrome.dates`]: '日期',
  [`${P}.chrome.days`]: '天数',
  [`${P}.chrome.description`]: '说明',
  [`${P}.chrome.destination`]: '目的地',
  [`${P}.chrome.editBasicInfo`]: '编辑基本信息',
  [`${P}.chrome.endDate`]: '结束日期',
  [`${P}.chrome.eyebrow`]: '旅程工作区',
  [`${P}.chrome.forbiddenBody`]: '你没有权限查看这趟私人行程。',
  [`${P}.chrome.forbiddenTitle`]: '无法打开此行程',
  [`${P}.chrome.myJourneys`]: '我的行程',
  [`${P}.chrome.noDescription`]: '尚未填写说明。',
  [`${P}.chrome.notFoundBody`]: '此行程可能不存在，或目前无法查看。',
  [`${P}.chrome.notFoundTitle`]: '找不到此行程',
  [`${P}.chrome.notSet`]: '尚未设置',
  [`${P}.chrome.openFailedTitle`]: '无法打开此行程',
  [`${P}.chrome.overview`]: '总览',
  [`${P}.chrome.people`]: '旅伴',
  [`${P}.chrome.peopleCount`]: '人数',
  [`${P}.chrome.saveChanges`]: '保存更改',
  [`${P}.chrome.saving`]: '保存中…',
  [`${P}.chrome.startDate`]: '开始日期',
  [`${P}.chrome.status`]: '状态',
  [`${P}.chrome.summary`]: '摘要',
  [`${P}.chrome.title`]: '标题',
  [`${P}.chrome.tryAgain`]: '再试一次',
  [`${P}.chrome.updated`]: '更新时间',
  [`${P}.chrome.yourRole`]: '你的角色',
  [`${P}.chrome.role.OWNER`]: '拥有者',
  [`${P}.chrome.role.COORDINATOR`]: '协调者',
  [`${P}.chrome.role.MEMBER`]: '成员',
  [`${P}.chrome.save.titleRequired`]: '请输入标题。',
  [`${P}.chrome.save.endBeforeStart`]: '结束日期不能早于开始日期。',
  [`${P}.chrome.save.conflict`]: '这趟旅程已在其他地方更新，请刷新后再试。',
  [`${P}.chrome.save.validation`]: '请检查旅程资料。',
  [`${P}.chrome.save.forbidden`]: '你没有权限编辑这趟旅程。',
  [`${P}.updating`]: '更新中…',

  [`${P}.guide.allTools`]: '自由编辑／所有工具',
  [`${P}.guide.prepare.heading`]: '规划起步',
  [`${P}.guide.prepare.progress`]: '规划起步：已完成 {done}／{total} 项',
  [`${P}.guide.prepare.complete`]: '行程已起步，可继续完善每日安排。',
  [`${P}.guide.prepare.unknownNote`]: '部分安排暂时无法读取，进度只计算已确认的项目。',
  [`${P}.guide.prepare.nextStep`]: '下一步',
  [`${P}.guide.prepare.nextStepOptional`]: '建议下一步（选填）',
  [`${P}.guide.prepare.upNext`]: '接下来',
  [`${P}.guide.task.basics.title`]: '基本资料',
  [`${P}.guide.task.basics.description`]: '填好目的地与出发、返程日期，旅伴与之后的安排才有依据。',
  [`${P}.guide.task.basics.done`]: '已完成',
  [`${P}.guide.task.basics.todo`]: '尚未完成：缺少目的地或日期',
  [`${P}.guide.task.basics.action`]: '填写目的地与日期',
  [`${P}.guide.task.basics.unavailable`]: '由拥有者或协调者设置基本资料。',
  [`${P}.guide.task.activities.title`]: '安排景点／活动',
  [`${P}.guide.task.activities.description`]: '在规划页把想去的景点、餐厅或活动排进每一天。',
  [`${P}.guide.task.activities.done`]: '已完成：已安排 {count} 项',
  [`${P}.guide.task.activities.todo`]: '尚未完成：还没有任何安排',
  [`${P}.guide.task.activities.unknown`]: '目前无法确认安排状态，可到规划页查看',
  [`${P}.guide.task.activities.emptyDays`]: '还有 {count} 天尚未安排（选填）',
  [`${P}.guide.task.activities.actionStart`]: '前往规划页安排行程',
  [`${P}.guide.task.activities.actionContinue`]: '继续安排每天的行程',
  [`${P}.guide.task.logistics.title`]: '住宿与交通',
  [`${P}.guide.task.logistics.description`]: '记下住宿与交通安排；当天往返或自行前往可以不填。',
  [`${P}.guide.task.logistics.added`]: '选填・已添加住宿 {stays} 条、交通 {transports} 条',
  [`${P}.guide.task.logistics.empty`]: '选填・尚未添加',
  [`${P}.guide.task.logistics.actionStays`]: '前往住宿页添加住宿',
  [`${P}.guide.task.logistics.actionTransport`]: '前往交通页',
  [`${P}.guide.task.departure.title`]: '出发准备',
  [`${P}.guide.task.departure.description`]: '用行前清单整理证件、行李与待办，完成度请在清单页查看。',
  [`${P}.guide.task.departure.optional`]: '选填',
  [`${P}.guide.task.departure.action`]: '前往行前清单',
  [`${P}.guide.traveling.heading`]: '旅程进行中',
  [`${P}.guide.traveling.eyebrow`]: '今天',
  [`${P}.guide.traveling.title`]: '查看今日行程，顺手记录',
  [`${P}.guide.traveling.body`]: '今日页会列出今天的安排；想留下心得或照片，可以到日记记录。',
  [`${P}.guide.traveling.actionToday`]: '查看今日行程',
  [`${P}.guide.completed.heading`]: '旅程已完成',
  [`${P}.guide.completed.eyebrow`]: '整理与分享',
  [`${P}.guide.completed.title`]: '整理回忆，决定是否分享',
  [`${P}.guide.completed.body`]: '可以补写日记；若想让其他人参考，可在公开旅程设置分享。分享不是必要步骤。',
  [`${P}.guide.completed.actionJournal`]: '整理旅程日记',
  [`${P}.guide.completed.actionPublication`]: '查看公开分享设置',
  [`${P}.guide.closed.archivedHeading`]: '旅程已归档',
  [`${P}.guide.closed.cancelledHeading`]: '旅程已取消',
  [`${P}.guide.closed.archivedBody`]: '这趟旅程已归档，不需要再准备，内容仅供回顾。',
  [`${P}.guide.closed.cancelledBody`]: '这趟旅程已取消，不需要再准备；可用的操作请见下方旅程状态。',
  [`${P}.recordJourney`]: '记录这趟旅程',

  [`${P}.nav.label`]: '工作区导航',
  [`${P}.nav.toolsShow`]: '所有工具／自由编辑',
  [`${P}.nav.toolsHide`]: '收起所有工具',
  [`${P}.nav.unread`]: '{count} 条未读旅程通知',
  [`${P}.nav.current`]: '当前',
  [`${P}.nav.offlineSuffix`]: '（需要网络连接）',
  [`${P}.nav.networkRequired`]: '需要网络连接',
  [`${P}.nav.group.plan`]: '行程安排',
  [`${P}.nav.group.collab`]: '协作',
  [`${P}.nav.group.memory`]: '记录',
  [`${P}.nav.group.money`]: '费用',
  [`${P}.nav.item.overview`]: '总览',
  [`${P}.nav.item.planning`]: '规划',
  [`${P}.nav.item.today`]: '今日',
  [`${P}.nav.item.people`]: '旅伴',
  [`${P}.nav.item.stays`]: '住宿',
  [`${P}.nav.item.transport`]: '交通',
  [`${P}.nav.item.checklist`]: '清单',
  [`${P}.nav.item.timeline`]: '动态',
  [`${P}.nav.item.notices`]: '通知',
  [`${P}.nav.item.chat`]: '旅程聊天室',
  [`${P}.nav.item.decisions`]: '决定',
  [`${P}.nav.item.situations`]: '旅程状况',
  [`${P}.nav.item.journal`]: '日记',
  [`${P}.nav.item.budget`]: '预算',
  [`${P}.nav.item.expenses`]: '支出',
  [`${P}.nav.item.split`]: '分账',

  [`${P}.allTools.title`]: '接着安排行程',
  [`${P}.allTools.offlineDetail`]: '规划、住宿与协作页面需要网络连接。',
  [`${P}.allTools.intro`]: '可前往以下页面：',
  [`${P}.allTools.planning`]: '安排日程与活动',
  [`${P}.allTools.stays`]: '管理住宿',
  [`${P}.allTools.transport`]: '规划交通',
  [`${P}.allTools.checklist`]: '准备行前清单',
  [`${P}.allTools.budget`]: '整理预算',
  [`${P}.allTools.decisions`]: '旅伴一起决定',

  [`${P}.lifecycle.heading`]: '旅程状态',
  [`${P}.lifecycle.secondaryLabel`]: '其他状态操作',
  [`${P}.lifecycle.closeConfirm`]: '关闭确认',
  [`${P}.lifecycle.cancelReason`]: '取消原因（选填）',
  [`${P}.lifecycle.back`]: '返回',
  [`${P}.lifecycle.status.DRAFT`]: '规划中',
  [`${P}.lifecycle.status.READY`]: '准备出发',
  [`${P}.lifecycle.status.TRAVELING`]: '旅程进行中',
  [`${P}.lifecycle.status.COMPLETED`]: '旅程已完成',
  [`${P}.lifecycle.status.CANCELLED`]: '旅程已取消',
  [`${P}.lifecycle.status.ARCHIVED`]: '已归档',
  [`${P}.lifecycle.status.unknown`]: '未知状态',
  [`${P}.lifecycle.hint.DRAFT`]: '完成基本安排后，可以将旅程标记为准备完成。',
  [`${P}.lifecycle.hint.READY`]: '行程已准备完成。出发时开始旅程。',
  [`${P}.lifecycle.hint.TRAVELING`]: '旅程进行中，可持续调整安排并记录日记。',
  [`${P}.lifecycle.hint.COMPLETED`]: '你仍可以补写日记、整理照片与分享旅程。',
  [`${P}.lifecycle.hint.CANCELLED`]: '此旅程已取消，无法继续一般行程操作。',
  [`${P}.lifecycle.hint.ARCHIVED`]: '此旅程已归档，仅供回顾。',
  [`${P}.lifecycle.hint.unknown`]: '当前状态无法进行旅程状态变更。',
  [`${P}.lifecycle.action.READY`]: '准备完成',
  [`${P}.lifecycle.action.START`]: '开始旅程',
  [`${P}.lifecycle.action.COMPLETE`]: '完成旅程',
  [`${P}.lifecycle.action.CANCEL`]: '取消旅程',
  [`${P}.lifecycle.action.ARCHIVE`]: '归档旅程',
  [`${P}.lifecycle.confirmAction.READY`]: '确认准备完成',
  [`${P}.lifecycle.confirmAction.START`]: '确认开始旅程',
  [`${P}.lifecycle.confirmAction.COMPLETE`]: '确认完成旅程',
  [`${P}.lifecycle.confirmAction.CANCEL`]: '确认取消旅程',
  [`${P}.lifecycle.confirmAction.ARCHIVE`]: '确认归档旅程',
  [`${P}.lifecycle.confirmTitle.READY`]: '确认准备完成？',
  [`${P}.lifecycle.confirmBody.READY`]: '确认将这趟旅程标记为准备完成？之后即可建立决定与书写日记。',
  [`${P}.lifecycle.confirmTitle.START`]: '确认开始旅程？',
  [`${P}.lifecycle.confirmBody.START`]: '确认开始这趟旅程？开始后会进入旅程进行中状态。',
  [`${P}.lifecycle.confirmTitle.COMPLETE`]: '确认完成旅程？',
  [`${P}.lifecycle.confirmBody.COMPLETE`]: '确认完成这趟旅程？完成后仍可补写日记。',
  [`${P}.lifecycle.confirmTitle.CANCEL`]: '确认取消旅程？',
  [`${P}.lifecycle.confirmBody.CANCEL`]: '取消后旅程会进入已取消状态，一般规划与出发流程将停止。此操作需由拥有者执行。',
  [`${P}.lifecycle.confirmTitle.ARCHIVE`]: '确认归档旅程？',
  [`${P}.lifecycle.confirmBody.ARCHIVE`]: '归档后无法再变更旅程状态（不可逆）。仅已完成或已取消的旅程可归档。',
  [`${P}.lifecycle.confirmTitle.default`]: '确认变更旅程状态？',
  [`${P}.lifecycle.confirmBody.default`]: '请确认要继续。',
  [`${P}.lifecycle.confirmAction.default`]: '确认',
  [`${P}.lifecycle.error.conflict`]: '这趟旅程刚刚有新的变更，已重新加载最新状态。',
  [`${P}.lifecycle.error.invalid-lifecycle`]: '当前状态无法执行此操作，已重新加载最新状态。',
  [`${P}.lifecycle.error.forbidden`]: '你没有权限变更这趟旅程的状态。',
  [`${P}.lifecycle.error.not-found`]: '找不到这趟旅程。',
  [`${P}.lifecycle.error.unauthorized`]: '登录状态已失效，请重新登录。',
  [`${P}.lifecycle.error.validation`]: '请确认后再试。',
  [`${P}.lifecycle.error.network`]: '网络异常，请稍后再试。',
  [`${P}.lifecycle.error.generic`]: '无法更新旅程状态，请稍后再试。',
};

export const getJourneyWorkspaceMessage = createFlatMessageLookup({
  zh_TW: journeyWorkspaceMessagesZhTW,
  en_US: journeyWorkspaceMessagesEnUS,
  zh_CN: journeyWorkspaceMessagesZhCN,
});
